/**
 * Runtime backend switcher (ORA-573).
 *
 * Lets a Vercel preview (or local dev) talk to any tagged `ora-be` staging
 * preview without a rebuild: append `?be=<pr-number>` (or `?be=<https url>`)
 * once and the choice is remembered in localStorage until it is cleared with
 * `?be=main`.
 *
 * The whole feature is gated on `VITE_BACKEND_SWITCHER === 'true'`, which
 * `vite.config.ts` derives from the build environment (preview / dev only).
 * In a production build that constant is the literal `'false'`, so every
 * branch below folds into dead code and the override can never be honored by
 * app.oraai.com — even if someone hand-writes the localStorage key.
 *
 * Auth note: staging previews share the JWT secret with the staging backend,
 * so tokens issued before a switch keep working; there is deliberately no
 * token flush or forced re-login when the override changes.
 */

/**
 * Cloud Run serves a tagged revision at `<tag>---<service-host>`. The backend
 * half of ORA-573 tags each `ora-be-staging` preview revision `pr-<n>`, where
 * `<n>` is the ora-be PR number; `ora-be-staging-hzpf7gg6sa-uw.a.run.app` is
 * that service's own Cloud Run host (project `ora-be`, region `us-west1`).
 */
export const STAGING_PREVIEW_HOST_TEMPLATE =
  'https://pr-{pr}---ora-be-staging-hzpf7gg6sa-uw.a.run.app';

/**
 * The only hosts a `?be=<url>` override may point at.
 *
 * Deliberately exact rather than "anything on `*.run.app`": `run.app` is a
 * shared Google domain, so a wildcard would let `?be=https://attacker.run.app`
 * take over a preview tester's session — the login POST, and every
 * authenticated request after it, would go to the attacker's service. Only the
 * `ora-be` staging service is accepted: its custom domain, its two Cloud Run
 * hostnames, and its tagged revisions (`<tag>---<host>`, which is how the
 * backend half of ORA-573 exposes each PR preview).
 */
export const BACKEND_OVERRIDE_ALLOWLIST = {
  hosts: [
    'api-staging.oraai.com',
    'ora-be-staging-hzpf7gg6sa-uw.a.run.app',
    'ora-be-staging-410202452602.us-west1.run.app',
  ],
  taggedHostPattern: /^[a-z0-9-]+---ora-be-staging-hzpf7gg6sa-uw\.a\.run\.app$/,
} as const;
const STORAGE_KEY = 'ora.backendOverride';
const OVERRIDE_QUERY_PARAM = 'be';
/** `?be=` values that mean "stop overriding and go back to the built-in URL". */
const CLEAR_VALUES = ['main', 'clear'];

export type BackendOverride =
  { kind: 'pr'; pr: number; url: string } | { kind: 'url'; url: string };

/** True only in builds that opted into the switcher (dev + Vercel previews). */
export const isBackendSwitcherEnabled = (): boolean =>
  import.meta.env.VITE_BACKEND_SWITCHER === 'true';

export const buildStagingPreviewUrl = (pr: number): string =>
  STAGING_PREVIEW_HOST_TEMPLATE.replace('{pr}', String(pr));

const isAllowedOverrideUrl = (url: URL): boolean =>
  url.protocol === 'https:' &&
  (BACKEND_OVERRIDE_ALLOWLIST.hosts.some((host) => host === url.hostname) ||
    BACKEND_OVERRIDE_ALLOWLIST.taggedHostPattern.test(url.hostname));

/**
 * Parse a raw `?be=` value. Returns `'clear'` for the reset keywords, an
 * override for an accepted value, and `null` for anything unrecognized (bad
 * host, non-https, garbage) — unrecognized values are silently ignored rather
 * than pointing the app at an attacker-supplied origin.
 */
export const parseBackendOverrideValue = (
  rawValue: string
): BackendOverride | 'clear' | null => {
  const value = rawValue.trim();
  if (!value) return null;

  if (CLEAR_VALUES.includes(value.toLowerCase())) return 'clear';

  if (/^\d+$/.test(value)) {
    const pr = Number(value);
    if (!Number.isSafeInteger(pr) || pr <= 0) return null;
    return { kind: 'pr', pr, url: buildStagingPreviewUrl(pr) };
  }

  try {
    const url = new URL(value);
    if (!isAllowedOverrideUrl(url)) return null;
    return { kind: 'url', url: url.origin };
  } catch {
    return null;
  }
};

const readStoredOverride = (): BackendOverride | null => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;

    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return null;

    const record = parsed as Record<string, unknown>;
    const url = typeof record.url === 'string' ? record.url : null;
    if (!url) return null;

    // Re-validate on read: a stale or hand-edited entry must clear the same
    // host check a fresh `?be=` value does.
    try {
      if (!isAllowedOverrideUrl(new URL(url))) return null;
    } catch {
      return null;
    }

    if (record.kind === 'pr' && typeof record.pr === 'number') {
      return { kind: 'pr', pr: record.pr, url };
    }

    return { kind: 'url', url };
  } catch {
    return null;
  }
};

/** The active override, or `null` when the gate is off or none is set. */
export const getBackendOverride = (): BackendOverride | null => {
  if (!isBackendSwitcherEnabled()) return null;
  return readStoredOverride();
};

export const setBackendOverride = (override: BackendOverride): void => {
  if (!isBackendSwitcherEnabled()) return;

  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(override));
  } catch {
    // Private mode / blocked storage: the switch just does not stick.
  }
};

export const clearBackendOverride = (): void => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing to do; the override is already unreachable.
  }
};

const getConfiguredBackendUrl = (): string => {
  const fromImportMeta = import.meta.env.VITE_BACKEND_URL;
  if (fromImportMeta) return fromImportMeta;

  // Non-Vite contexts (the OAuth helper historically read this fallback).
  if (typeof process !== 'undefined' && process.env?.VITE_BACKEND_URL) {
    return process.env.VITE_BACKEND_URL;
  }

  return '';
};

/**
 * The backend base URL every caller should use. Resolved per call (not cached)
 * so setting or clearing an override takes effect without a reload.
 */
export const getBackendUrl = (): string =>
  getBackendOverride()?.url ?? getConfiguredBackendUrl();

const stripOverrideQueryParam = (url: URL): void => {
  try {
    url.searchParams.delete(OVERRIDE_QUERY_PARAM);
    const search = url.searchParams.toString();

    window.history.replaceState(
      window.history.state,
      '',
      `${url.pathname}${search ? `?${search}` : ''}${url.hash}`
    );
  } catch {
    // replaceState can throw in exotic embeddings; the param is harmless.
  }
};

/**
 * Read `?be=` from the current URL, persist the result, then strip the param
 * so it does not ride along into later navigation (or into shared links).
 * Call once at startup, before the router mounts.
 */
export const resolveBackendOverrideFromLocation = (): void => {
  if (!isBackendSwitcherEnabled()) return;
  if (typeof window === 'undefined') return;

  let url: URL;
  try {
    url = new URL(window.location.href);
  } catch {
    return;
  }

  const rawValue = url.searchParams.get(OVERRIDE_QUERY_PARAM);
  if (rawValue === null) return;

  const parsed = parseBackendOverrideValue(rawValue);
  if (parsed === 'clear') {
    clearBackendOverride();
  } else if (parsed) {
    setBackendOverride(parsed);
  }

  stripOverrideQueryParam(url);
};

/** Label for the badge: `pr-123` for a PR override, the host for a raw URL. */
export const describeBackendOverride = (override: BackendOverride): string => {
  if (override.kind === 'pr') return `pr-${override.pr}`;

  try {
    return new URL(override.url).host;
  } catch {
    return override.url;
  }
};
